if (!customElements.get('mobile-search-overlay')) {
  const RECENT_SEARCHES_KEY = 'adheseal_recent_searches';
  const MAX_RECENT_SEARCHES = 8;
  const DEBOUNCE_MS = 300;
  const MIN_QUERY_LENGTH = 2;
  const TRANSITION_MS = 250;

  function debounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  class MobileSearchOverlay extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.cachedResults = {};
      this.scrollPosition = 0;
    }

    connectedCallback() {
      this.backdrop = this.querySelector('[data-overlay-backdrop]');
      this.panel = this.querySelector('.mobile-search-overlay__panel');
      this.input = this.querySelector('.mobile-search-overlay__input');
      this.clearBtn = this.querySelector('[data-overlay-clear]');
      this.closeBtn = this.querySelector('[data-overlay-close]');
      this.resultsContainer = this.querySelector('[data-predictive-search]');
      this.recentContainer = this.querySelector('[data-recent-searches]');
      this.recentList = this.querySelector('.mobile-search-overlay__recent-list');
      this.recentClearBtn = this.querySelector('[data-recent-clear]');
      this.emptyState = this.querySelector('[data-search-empty]');
      this.statusEl = this.querySelector('.predictive-search-status');
      this.form = this.querySelector('form');

      this.productToShow = 6;
      this.sectionId = 'predictive-search';

      this.bindEvents();
      this.bindTrigger();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    bindTrigger() {
      const trigger = document.querySelector('[data-mobile-search-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', () => this.open());
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.open();
        }
      });
    }

    bindEvents() {
      this._onBackdropClick = this.onBackdropClick.bind(this);
      this._onCloseClick = this.onCloseClick.bind(this);
      this._onClearClick = this.onClearClick.bind(this);
      this._onRecentClear = this.onRecentClear.bind(this);
      this._onInput = debounce(this.onInput.bind(this), DEBOUNCE_MS);
      this._onKeydown = this.onKeydown.bind(this);
      this._onFormSubmit = this.onFormSubmit.bind(this);
      this._onFocusOut = this.onFocusOut.bind(this);

      this.backdrop.addEventListener('click', this._onBackdropClick);
      this.closeBtn.addEventListener('click', this._onCloseClick);
      this.clearBtn.addEventListener('click', this._onClearClick);
      this.recentClearBtn.addEventListener('click', this._onRecentClear);
      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);
      this.form.addEventListener('submit', this._onFormSubmit);
      this.addEventListener('focusout', this._onFocusOut);
    }

    unbindEvents() {
      this.backdrop.removeEventListener('click', this._onBackdropClick);
      this.closeBtn.removeEventListener('click', this._onCloseClick);
      this.clearBtn.removeEventListener('click', this._onClearClick);
      this.recentClearBtn.removeEventListener('click', this._onRecentClear);
      this.input.removeEventListener('input', this._onInput);
      this.input.removeEventListener('keydown', this._onKeydown);
      this.form.removeEventListener('submit', this._onFormSubmit);
      this.removeEventListener('focusout', this._onFocusOut);
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      this.scrollPosition = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mobile-search-overlay-open');

      this.setAttribute('aria-hidden', 'false');
      this.classList.add('mobile-search-overlay--open');

      this.renderRecentSearches();
      this.showRecentSection();

      setTimeout(() => {
        this.input.focus();
      }, TRANSITION_MS);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.classList.remove('mobile-search-overlay--open');
      this.setAttribute('aria-hidden', 'true');

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-search-overlay-open');
      window.scrollTo(0, this.scrollPosition);

      this.input.value = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.clearBtn.classList.add('hidden');
      this.emptyState.classList.add('hidden');
      this.resultsContainer.style.display = 'none';
      this.resultsContainer.innerHTML = '';
      this.removeAttribute('loading');
      this.removeAttribute('results');
    }

    onBackdropClick() {
      this.close();
    }

    onCloseClick() {
      this.close();
    }

    onClearClick() {
      this.input.value = '';
      this.input.focus();
      this.clearBtn.classList.add('hidden');
      this.hideResults();
      this.showRecentSection();
    }

    onRecentClear() {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
      this.recentContainer.classList.add('hidden');
      this.recentList.innerHTML = '';
    }

    onInput() {
      const query = this.getQuery();

      if (query.length > 0) {
        this.clearBtn.classList.remove('hidden');
      } else {
        this.clearBtn.classList.add('hidden');
      }

      if (query.length < MIN_QUERY_LENGTH) {
        this.hideResults();
        this.showRecentSection();
        return;
      }

      this.getSearchResults(query);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.navigateResults(event.key === 'ArrowDown' ? 'down' : 'up');
      }

      if (event.key === 'Enter') {
        const selected = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
        if (selected) {
          event.preventDefault();
          selected.click();
        } else {
          this.addRecentSearch(this.getQuery());
        }
      }
    }

    onFormSubmit(event) {
      const query = this.getQuery();
      if (!query.length) {
        event.preventDefault();
        return;
      }

      const selected = this.querySelector('[aria-selected="true"] a');
      if (selected) {
        event.preventDefault();
        return;
      }

      this.addRecentSearch(query);
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    getQuery() {
      return this.input.value.trim().replace(/\s+/g, ' ');
    }

    async getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey], searchTerm);
        return;
      }

      this.setLoading(true);
      this.hideRecentSection();
      this.emptyState.classList.add('hidden');

      const params = new URLSearchParams({
        q: searchTerm,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': this.productToShow,
        'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
        section_id: this.sectionId
      });

      try {
        const response = await fetch(`${window.routes.predictive_search_url}?${params}`);
        if (!response.ok) throw new Error(response.status);

        const text = await response.text();
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup, searchTerm);
      } catch {
        this.hideResults();
      }
    }

    renderResults(markup, searchTerm) {
      this.setLoading(false);
      this.resultsContainer.innerHTML = markup;
      this.resultsContainer.style.display = 'block';
      this.setAttribute('results', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.hideRecentSection();

      const resultsCount = this.resultsContainer.querySelector('[data-predictive-search-live-region-count-value]');
      if (resultsCount) {
        this.setStatus(resultsCount.textContent);
      }

      // The section emits a .productEmpty block only when every resource type
      // (products, collections, articles, pages) is empty, so it's a reliable
      // "no results" signal. Don't parseInt the live-region sentence — it's a
      // full string like "View All Results (3)" and always yields NaN.
      if (this.resultsContainer.querySelector('.productEmpty')) {
        this.emptyState.querySelector('.mobile-search-overlay__empty-text').textContent =
          `${window.theme?.strings?.searchNoResults || 'No results for'} "${searchTerm}"`;
        this.emptyState.classList.remove('hidden');
        this.resultsContainer.style.display = 'none';
      } else {
        this.emptyState.classList.add('hidden');
      }
    }

    hideResults() {
      this.resultsContainer.style.display = 'none';
      this.resultsContainer.innerHTML = '';
      this.removeAttribute('results');
      this.removeAttribute('loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.emptyState.classList.add('hidden');
    }

    setLoading(loading) {
      if (loading) {
        this.setAttribute('loading', 'true');
        this.resultsContainer.style.display = 'block';
      } else {
        this.removeAttribute('loading');
      }
    }

    setStatus(text) {
      if (!this.statusEl) return;
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = text;
      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    navigateResults(direction) {
      const items = this.querySelectorAll('.predictive-search__list-item');
      if (!items.length) return;

      const current = this.querySelector('[aria-selected="true"]');
      let next;

      if (direction === 'down') {
        next = current ? current.nextElementSibling : items[0];
        if (!next) next = items[0];
      } else {
        next = current ? current.previousElementSibling : items[items.length - 1];
        if (!next) next = items[items.length - 1];
      }

      if (current) current.setAttribute('aria-selected', 'false');
      next.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', next.id);
      next.scrollIntoView({ block: 'nearest' });
    }

    getRecentSearches() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY)) || [];
      } catch {
        return [];
      }
    }

    addRecentSearch(term) {
      if (!term || term.length < MIN_QUERY_LENGTH) return;

      let recent = this.getRecentSearches();
      recent = recent.filter(r => r.toLowerCase() !== term.toLowerCase());
      recent.unshift(term);
      recent = recent.slice(0, MAX_RECENT_SEARCHES);

      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
      } catch {
        // localStorage full or unavailable
      }
    }

    renderRecentSearches() {
      const recent = this.getRecentSearches();
      if (!recent.length) {
        this.recentContainer.classList.add('hidden');
        return;
      }

      this.recentList.innerHTML = recent.map((term, index) => `
        <li class="mobile-search-overlay__recent-item" role="option" aria-selected="false" id="recent-result-${index}">
          <a href="${window.routes.search_url}?q=${encodeURIComponent(term)}&type=product" class="mobile-search-overlay__recent-link">
            <svg class="mobile-search-overlay__recent-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path></svg>
            <span class="mobile-search-overlay__recent-text">${this.escapeHtml(term)}</span>
          </a>
        </li>
      `).join('');

      this.recentList.querySelectorAll('.mobile-search-overlay__recent-link').forEach(link => {
        link.addEventListener('click', () => {
          const term = link.querySelector('.mobile-search-overlay__recent-text').textContent;
          this.addRecentSearch(term);
        });
      });
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    showRecentSection() {
      const recent = this.getRecentSearches();
      if (recent.length) {
        this.recentContainer.classList.remove('hidden');
      }
    }

    hideRecentSection() {
      this.recentContainer.classList.add('hidden');
    }
  }

  customElements.define('mobile-search-overlay', MobileSearchOverlay);
}
